import { useDispatch } from "react-redux";
import { useEffect } from "react";
import { initData } from "@telegram-apps/sdk-react";
import toast from "react-hot-toast";

import { setInitData, setIsInitDataObtained } from "@/store/telegramSlice";
import { logMsg, logEx, crumb } from "@/config/sentry-logger";

import type { AppDispatch } from "@/store";

const TEST_INIT_DATA = import.meta.env.VITE_TEST_INIT_DATA as
  | string
  | undefined;

class NoInitDataError extends Error {
  context?: unknown;
  constructor(message: string, context?: unknown) {
    super(message);
    this.name = "NoInitDataError";
    this.context = context;
  }
}

const useConfigureTelegramWebApp = () => {
  const dispatch = useDispatch<AppDispatch>();

  useEffect(() => {
    try {
      if (!TEST_INIT_DATA) {
        try {
          initData.restore();
          crumb("initData.restore called");
        } catch (e) {
          logMsg("initData.restore failed", "warning");
          logEx(e, "initData.restore");
        }
      }

      let rawInitData = TEST_INIT_DATA || initData.raw();

      if (!rawInitData && typeof window !== "undefined") {
        rawInitData = window.Telegram?.WebApp?.initData || "";
      }

      if (rawInitData) {
        crumb("initData obtained");
        dispatch(setInitData(rawInitData));
      } else {
        const context = {
          userAgent: navigator.userAgent,
          platform: navigator.platform,
          url: window.location.href,
          hasWindowTelegram: !!window.Telegram,
          hasWebApp: !!window.Telegram?.WebApp,
        };
        logMsg("no-init-data", "warning", context);
        const err = new NoInitDataError("no-init-data", context);
        logEx(err, "initData missing", context);
        throw err;
      }
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("initData is NOT DEFINED", err);
      toast.error("Something went wrong, please try again");
    }

    dispatch(setIsInitDataObtained(true));
  }, [dispatch]);
};

export { useConfigureTelegramWebApp };
